import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const serviceOptions = [
  'Fencing (Vinyl, Wood, Chain Link, Aluminum)',
  'Pavers (Driveway, Patio, Pool Deck, Walkway)',
  'Landscape Installation (Sod, Planting, Mulch)',
  'Landscape Lighting',
  'Irrigation / Sprinkler Systems',
  'Custom Sheds',
  'Multiple Services',
];

const areaOptions = [
  'Palm Coast, FL',
  'Ormond Beach, FL',
  'Daytona Beach, FL',
  'Port Orange, FL',
  'New Smyrna Beach, FL',
  'Edgewater, FL',
  'Other / Not Listed',
];

type FormData = {
  name: string;
  email: string;
  phone: string;
  service: string;
  area: string;
  message: string;
};

export default function ContactPage() {
  const [form, setForm] = useState<FormData>({ name: '', email: '', phone: '', service: '', area: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d71fgq0hb0rqicn68gtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', service: '', area: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen font-sans">
      <Navbar />
      <main>
        {/* Cinematic Hero */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/c59a8cc614fa729f201730d1b76ef66b.jpg"
              alt="Design consultation — Dippel's Outdoor Services Florida"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gold to-transparent opacity-60"></div>

          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10 bg-gold"></div>
                <span className="text-gold font-sans text-[11px] font-semibold tracking-[0.28em] uppercase">
                  Start Your Project
                </span>
              </div>
              <h1 className="font-serif text-white font-bold leading-tight mb-5" style={{ fontSize: 'clamp(38px, 4.5vw, 60px)' }}>
                Request Your Free<br />
                <em className="text-gold not-italic">Design Consultation</em>
              </h1>
              <p className="text-white/60 text-base leading-relaxed mb-8">
                Tell us about your vision. We&apos;ll visit your property, assess the scope, and deliver a detailed, transparent estimate — completely free with no obligation.
              </p>
              <div className="flex flex-wrap gap-5">
                {[
                  { icon: 'ri-timer-line', text: 'Response within 24 hours' },
                  { icon: 'ri-shield-check-fill', text: 'Licensed & Insured' },
                  { icon: 'ri-price-tag-3-line', text: 'No hidden fees' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/50 text-sm">
                    <i className={`${b.icon} text-gold text-sm`}></i>
                    {b.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Main content */}
        <section className="bg-stone py-20 px-6">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12">

            {/* Left: Contact info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <p className="text-gold font-sans text-[11px] font-semibold tracking-[0.28em] uppercase mb-4 flex items-center gap-3">
                  <span className="h-px w-8 bg-gold inline-block"></span>
                  Contact Information
                </p>
                <h2 className="font-serif text-charcoal text-3xl font-bold mb-6">Reach Us Directly</h2>
              </div>

              {/* Contact cards */}
              <div className="space-y-3">
                {[
                  { icon: 'ri-phone-fill', label: 'Office Number', value: '(386) 301-4399', href: 'tel:3863014399' },
                  { icon: 'ri-phone-fill', label: 'Direct Phone', value: '(386) 566-8781', href: 'tel:3865668781' },
                  { icon: 'ri-mail-fill', label: 'Email', value: 'dippelsoutdoor@gmail.com', href: 'mailto:dippelsoutdoor@gmail.com' },
                ].map((c, i) => (
                  <a
                    key={i}
                    href={c.href}
                    className="flex items-center gap-4 bg-white p-5 rounded-xl border border-stone-dark hover:border-forest/30 transition-all group block"
                  >
                    <div className="w-11 h-11 flex items-center justify-center bg-forest/8 rounded-xl flex-shrink-0 group-hover:bg-forest/15 transition-colors">
                      <i className={`${c.icon} text-forest text-base`}></i>
                    </div>
                    <div>
                      <p className="text-charcoal-muted text-xs uppercase tracking-wider mb-0.5">{c.label}</p>
                      <p className="text-charcoal font-semibold text-base">{c.value}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-charcoal/30 group-hover:text-forest ml-auto transition-colors"></i>
                  </a>
                ))}
              </div>

              {/* Hours */}
              <div className="bg-charcoal rounded-xl p-6">
                <h3 className="font-serif text-white text-lg font-semibold mb-5 flex items-center gap-2">
                  <i className="ri-time-fill text-gold text-base"></i>
                  Business Hours
                </h3>
                <div className="space-y-3">
                  {[
                    { day: 'Monday – Friday', hours: '9:00 AM – 3:00 PM', open: true },
                    { day: 'Saturday', hours: 'By Appointment Only', open: true },
                    { day: 'Sunday', hours: 'Closed', open: false },
                  ].map((h, i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-white/55">{h.day}</span>
                      <span className={`font-medium ${h.open ? 'text-white' : 'text-white/30'}`}>{h.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-stone-dark" style={{ height: '220px' }}>
                <iframe
                  title="Dippel's Outdoor Services location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d221576.2729994!2d-81.2453!3d29.0228!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e73fb6defa2f9b%3A0xa7948486a71768ac!2sDippel%27s%20Outdoor%20Services!5e0!3m2!1sen!2sus!4v1710000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-9 border border-stone-dark">
                <h2 className="font-serif text-charcoal text-2xl font-bold mb-1.5">Design Consultation Request</h2>
                <p className="text-charcoal-muted text-sm mb-8">Tell us about your project — we&apos;ll get back to you within 24 hours with a free, detailed estimate.</p>

                {status === 'success' ? (
                  <div className="text-center py-14">
                    <div className="w-16 h-16 flex items-center justify-center bg-forest/10 rounded-full mx-auto mb-5">
                      <i className="ri-checkbox-circle-fill text-forest text-3xl"></i>
                    </div>
                    <h3 className="font-serif text-2xl text-charcoal font-bold mb-3">Consultation Request Sent!</h3>
                    <p className="text-charcoal-muted text-sm max-w-sm mx-auto mb-7">
                      Thank you for reaching out to Dippel&apos;s Outdoor Services. We&apos;ll be in touch within 24 hours to schedule your free on-site consultation.
                    </p>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 bg-forest text-white font-semibold px-7 py-3.5 rounded-full hover:bg-forest-light transition-colors whitespace-nowrap"
                    >
                      Back to Home
                    </Link>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-forest transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="(386) 000-0000"
                          className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-forest transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-forest transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Service Needed *</label>
                        <select
                          name="service"
                          required
                          value={form.service}
                          onChange={handleChange}
                          className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal focus:outline-none focus:border-forest transition-colors"
                        >
                          <option value="">Select a service...</option>
                          {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Your City</label>
                        <select
                          name="area"
                          value={form.area}
                          onChange={handleChange}
                          className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal focus:outline-none focus:border-forest transition-colors"
                        >
                          <option value="">Select your city...</option>
                          {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-charcoal uppercase tracking-[0.14em] mb-2">Project Details</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us about your project — dimensions, materials, timeline, budget range, or any specific questions..."
                        className="w-full border border-stone-dark bg-stone rounded-lg px-4 py-3 text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-forest transition-colors resize-none"
                      ></textarea>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-charcoal-muted">Max 500 characters</span>
                        <span className={`text-xs ${form.message.length > 480 ? 'text-red-500' : 'text-charcoal-muted'}`}>
                          {form.message.length}/500
                        </span>
                      </div>
                    </div>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                        Something went wrong. Please call us at{' '}
                        <a href="tel:3863014399" className="font-semibold underline whitespace-nowrap">(386) 301-4399</a>.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending' || form.message.length > 500}
                      className="w-full bg-charcoal text-white font-bold py-4 rounded-full hover:bg-forest transition-colors disabled:opacity-50 whitespace-nowrap text-[15px] tracking-wide"
                    >
                      {status === 'sending' ? 'Sending Your Request...' : 'Request My Free Design Consultation'}
                    </button>

                    <p className="text-center text-charcoal-muted text-xs">
                      Or call us now:{' '}
                      <a href="tel:3863014399" className="text-forest font-semibold hover:underline whitespace-nowrap">
                        (386) 301-4399
                      </a>{' '}
                      &nbsp;·&nbsp; Mon–Fri 9AM–3PM
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
